import React, { Suspense, useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { isFeatureEnabled, loadFeatureTogglesFromSettings } from '../utils/featureFlags';
import { FEATURES } from '../features';

// Lazy load public components
const TenantLogin = React.lazy(() => import('./public/TenantLogin'));
const TenantMaintenanceDashboard = React.lazy(() => import('./public/TenantMaintenanceDashboard'));
const TenantMaintenanceReport = React.lazy(() => import('./public/TenantMaintenanceReport'));
const TenantMaintenanceView = React.lazy(() => import('./public/TenantMaintenanceView'));
const ContractorLogin = React.lazy(() => import('./public/ContractorLogin'));
const ContractorDashboard = React.lazy(() => import('./public/ContractorDashboard'));
const ContractorJobDetails = React.lazy(() => import('./public/ContractorJobDetails'));
const ContractorAgentMessages = React.lazy(() => import('./public/ContractorAgentMessages'));

// Loading component for public routes
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
    <div className="flex flex-col items-center">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      <span className="mt-4 text-gray-600 dark:text-gray-400">Loading...</span>
    </div>
  </div>
);

/**
 * PublicApp - Public access routes for tenants and contractors
 * 
 * These routes don't require Firebase authentication.
 * Instead, they use email-based verification codes.
 */
const PublicApp = () => {
  const [isTenantPortalEnabled, setIsTenantPortalEnabled] = useState(false);
  const [featureFlagsLoaded, setFeatureFlagsLoaded] = useState(false);

  // Load feature flags on mount
  useEffect(() => {
    const loadFeatureFlags = async () => {
      try {
        // Load feature toggles from Firestore
        await loadFeatureTogglesFromSettings();
        // Now check if tenant portal is enabled
        const enabled = isFeatureEnabled(FEATURES.TENANT_PORTAL);
        setIsTenantPortalEnabled(enabled);
        setFeatureFlagsLoaded(true);
      } catch (error) {
        console.error('Error loading feature flags:', error);
        // Fallback to default check
        const enabled = isFeatureEnabled(FEATURES.TENANT_PORTAL);
        setIsTenantPortalEnabled(enabled);
        setFeatureFlagsLoaded(true);
      }
    };

    loadFeatureFlags();
  }, []);

  // Show loading state while feature flags are being loaded
  if (!featureFlagsLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <span className="mt-4 text-gray-600 dark:text-gray-400">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50 dark:bg-gray-900">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Tenant Routes - Protected by feature flag */}
          {isTenantPortalEnabled ? (
            <>
              <Route path="/tenant" element={<TenantLogin />} />
              <Route path="/tenant/maintenance" element={<TenantMaintenanceDashboard />} />
              <Route path="/tenant/report" element={<TenantMaintenanceReport />} />
              <Route path="/tenant/view/:requestId" element={<TenantMaintenanceView />} />
            </>
          ) : (
            <>
              {/* Redirect all tenant routes to login if feature is disabled */}
              <Route path="/tenant" element={<Navigate to="/login" replace />} />
              <Route path="/tenant/maintenance" element={<Navigate to="/login" replace />} />
              <Route path="/tenant/report" element={<Navigate to="/login" replace />} />
              <Route path="/tenant/view/:requestId" element={<Navigate to="/login" replace />} />
            </>
          )}

          {/* Contractor Routes */}
          <Route path="/contractor" element={<ContractorLogin />} />
          <Route path="/contractor/dashboard" element={<ContractorDashboard />} />
          <Route path="/contractor/job/:jobId" element={<ContractorJobDetails />} />
          <Route path="/contractor/job/:jobId/messages" element={<ContractorAgentMessages />} />

          {/* Default redirect */}
          <Route path="/tenant/*" element={isTenantPortalEnabled ? <Navigate to="/tenant" replace /> : <Navigate to="/login" replace />} />
          <Route path="/contractor/*" element={<Navigate to="/contractor" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
};

export default PublicApp;

